import { Suspense, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from 'lucide-react';
import LoadingSpinner from '@/components/custom-ui/LoadingSpinner';
import { useAuth } from "@/contexts/AuthContext";
import { AuthShell } from '@/pages/auth/AuthShell';

export const dynamic = 'force-dynamic';

export default function VerifyPageWrapper() {
    return (
        <Suspense fallback={<LoadingSpinner />}>
            <VerifyPage />
        </Suspense>
    );
}

// Where the confirmation email lands. Two things were wrong with it beyond the
// language:
//
//  * verifyEmail() catches its own errors and RETURNS { success: false }; it
//    never throws. The page only had a try/catch, so an expired or reused link
//    fell through to "Email Verified" — the one case where being told it worked
//    is worse than being told nothing.
//  * it logged the verification token to the console on every load.
function VerifyPage() {
    const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { verifyEmail } = useAuth();

    // A token is single-use, so this must run exactly once — including under
    // React StrictMode's double-invoke in development, where the second call
    // would consume an already-spent token and report failure.
    const attemptedRef = useRef(false);

    useEffect(() => {
        if (attemptedRef.current) return;
        attemptedRef.current = true;

        const token = searchParams.get('token');
        const email = searchParams.get('email');

        if (!token) {
            setStatus('error');
            setErrorMessage('Länken saknar en bekräftelsekod. Öppna länken från mejlet igen.');
            return;
        }

        void (async () => {
            const result = await verifyEmail(token, email);
            if (result.success) {
                setStatus('success');
                return;
            }
            setStatus('error');
            setErrorMessage(
                result.error?.message
                    ?? 'Länken kan ha gått ut eller redan använts. Begär en ny bekräftelselänk.',
            );
        })();
    }, [searchParams, verifyEmail]);

    if (status === 'verifying') {
        return (
            <AuthShell title="Bekräftar din e-postadress" subtitle="Ett ögonblick...">
                <div className="flex justify-center py-4">
                    <LoadingSpinner />
                </div>
            </AuthShell>
        );
    }

    if (status === 'success') {
        return (
            <AuthShell title="E-postadressen är bekräftad" subtitle="Ditt konto är aktivt.">
                <div className="flex flex-col items-center text-center gap-4">
                    <div className="w-14 h-14 bg-[#A4C3B2] rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-7 h-7 text-[#1A4D4F]" />
                    </div>
                    {/* verifyEmail() signs the user in as part of confirming, so
                        sending them to the dashboard here is not a dead end. */}
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        className="w-full flex items-center justify-center px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm"
                    >
                        Fortsätt till din översikt
                    </button>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell title="Bekräftelsen misslyckades" subtitle="Vi kunde inte bekräfta din e-postadress.">
            <div className="flex flex-col items-center text-center gap-4">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                    <XCircle className="w-7 h-7 text-red-600" />
                </div>
                <p className="text-sm text-gray-700">{errorMessage}</p>
                <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="w-full flex items-center justify-center px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm"
                >
                    Till startsidan
                </button>
            </div>
        </AuthShell>
    );
}
